import React, { useState } from "react";
import {
  Blocks, Download, ExternalLink, FilePen, FileText, Globe, MessageCircleQuestion, Search, Server, TerminalSquare,
  Trash2, Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { toAttachment } from "@/shared/api/attachments";
import { useAgents } from "@/shared/api/agents";
import SiteIcon from "@/shared/components/SiteIcon";
import { siteName, webAddress } from "@/shared/lib/sites";
import type { ConnectionAppRef, RunStep, WebSource } from "@/shared/types";

import { answersIn, questionsOf } from "./askUser";
import { FileChip } from "./MessageBlocks";

/* What each kind of tool call says on its row (`StepRows`): its tile, one sentence (what it
   did, the specifics dimmed), what it is doing while it runs (the turn's header), and what
   it shows opened besides its arguments and result. The sentence never names the tool: the
   arguments do, opened. */

/** The apps this agent can reach, by slug: how an app tool's row gets its name and logo. */
export type AppRefs = Record<string, ConnectionAppRef>;

export interface StepDescription {
  /** The row's tile: an icon, or the app's logo. */
  icon: React.ReactNode;
  sentence: React.ReactNode;
  /** What the call is doing while it runs, in a few words: the turn's header (`stepPhase`). */
  phase: string;
  /** Small facts opened, before the arguments: the account, an exit code. */
  facts?: React.ReactNode[];
  /** What it shows opened in place of the arguments, or before them. */
  extra?: React.ReactNode;
  /** The arguments worth showing: an app action's own, not the wrapper's. */
  args?: Record<string, unknown>;
  /** A block that reads better than the arguments, shown in their place: a command, a file's text. */
  code?: { label: string; text: string };
  /** The arguments say nothing the sentence and `extra` don't. */
  hideArgs?: boolean;
}

const lucide = (Icon: LucideIcon) => <Icon className="w-[14px] h-[14px]" strokeWidth={1.75} />;

/** The specifics in a sentence: the query, the file, the command. */
const Dim = ({ children }: { children?: React.ReactNode }) => <span className="text-[var(--text-tertiary)]">{children}</span>;

/** "SEND_EMAIL", "list_issues" or "getIssue" as words: "Send email", "List issues", "Get issue". */
const words = (name: string) => {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** The app a tool slug belongs to: the longest linked slug followed by `_`, as the backend
    decides it. Only needed before the reply names the app itself. */
const appOf = (tool: string, apps: AppRefs) =>
  Object.keys(apps)
    .filter((slug) => tool.toLowerCase().startsWith(`${slug.toLowerCase()}_`))
    .sort((a, b) => b.length - a.length)[0];

export function describeStep(e: RunStep, running: boolean, apps: AppRefs): StepDescription {
  if (e.tool === "ask_user" && e.status !== "failed") return describeQuestion(e, running);

  if (e.tool === "search_app_tools") {
    const asked = e.args.use_case ?? (Array.isArray(e.args.tools) ? e.args.tools.join(", ") : null);
    return {
      icon: lucide(Search),
      sentence: <>{running ? "Finding app tools" : "Found app tools"}{asked ? <> for <Dim>{String(asked)}</Dim></> : null}</>,
      phase: "Finding app tools",
    };
  }

  if (e.tool === "remote_tool_exec") {
    const tool = String(e.args.name ?? "");
    const slug = e.app ?? appOf(tool, apps);
    const app = slug ? apps[slug] : undefined;
    const name = app?.name ?? (slug ? words(slug) : "an app");
    const action = slug ? tool.slice(slug.length + 1) : tool;
    return {
      icon: app?.logo ? <img src={app.logo} alt="" className="w-4 h-4 rounded-[3px]" /> : lucide(Blocks),
      // while the model still writes the call, it names no action yet
      sentence: action ? <>{words(action)} <Dim>in {name}</Dim></> : `Using ${name}`,
      phase: `Using ${name}`,
      facts: e.account ? [<>as <b className="font-medium text-[var(--text-primary)]">{e.account}</b></>] : undefined,
      args: (e.args.args as Record<string, unknown> | undefined) ?? {},
    };
  }

  if (SANDBOX_TOOLS.has(e.tool)) return describeSandbox(e, running);

  if (e.tool === "web_search" || e.tool === "web_fetch") return describeWeb(e, running);

  if (e.mcpServer) {
    return {
      icon: lucide(Server),
      sentence: <>{words(e.mcpTool ?? e.tool)} <Dim>in {e.mcpServer}</Dim></>,
      phase: `Using ${e.mcpServer}`,
      facts: ["MCP server"],
    };
  }

  // a call about one of the workspace's agents (Definable's tools) names it
  const agentId = typeof e.args.agent_id === "string" ? e.args.agent_id : null;
  return {
    icon: lucide(Wrench),
    sentence: <>{words(e.tool)}{agentId && <> <Dim><AgentName id={agentId} /></Dim></>}</>,
    phase: "Working",
  };
}

/** ask_user (backend/agent/tools/ask_user.py): what it asked and, once answered, each
    question's answer, as the Stepper Form's review lists them. Words the person wrote
    themselves (in the composer, on a channel) answer all the questions at once; a call
    nobody answered lists its questions. One the model got wrong is any failed call's row. */
function describeQuestion(e: RunStep, running: boolean): StepDescription {
  const questions = questionsOf(e.args);
  const asked = questions.length === 1 ? questions[0].question : questions.length ? null : String(e.args.question ?? "");
  const answers = e.result != null && questions.length ? answersIn(questions, e.result) : null;
  const rows = answers
    ? questions.map((q, i) => ({ label: q.header || q.question, answer: answers[i] }))
    : e.result != null
      ? [{ label: "Answer", answer: e.result }]
      : questions.map((q, i) => ({ label: q.header || `Question ${i + 1}`, answer: q.question }));
  return {
    icon: lucide(MessageCircleQuestion),
    // while the model still writes the arguments there is nothing to name yet
    sentence: <>{running ? "Asking you" : "Asked you"}{asked ? <> <Dim>{asked}</Dim></> : questions.length > 1 && ` ${questions.length} questions`}</>,
    phase: "Asking you",
    hideArgs: true,
    extra: (
      <dl className="m-0 rounded-[10px] ring-1 ring-inset ring-[var(--hairline-strong)] divide-y divide-[var(--hairline)] overflow-hidden">
        {rows.map((row, i) => (
          <div key={i} className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 items-baseline px-3.5 py-2.5">
            <dt className="text-[12.5px] text-[var(--text-tertiary)] [overflow-wrap:anywhere]">{row.label}</dt>
            <dd className="m-0 text-[13.5px] text-[var(--text-primary)] whitespace-pre-wrap [overflow-wrap:anywhere]">{row.answer}</dd>
          </div>
        ))}
      </dl>
    ),
  };
}

/** The name of the workspace agent a tool call acted on, from the agents list. */
function AgentName({ id }: { id: string }) {
  const { data: agents } = useAgents();
  return <>{agents?.find((a) => a.id === id)?.name ?? "an agent"}</>;
}

/* The sandbox tools (backend/agent/tools/sandbox): what ran, on which file. Facts come from
   the reply's `meta.sandbox`, never from its text. */
const SANDBOX_PHASES: Record<string, string> = {
  bash: "Running a command", bash_output: "Checking a job", bash_stop: "Stopping a job",
  read_file: "Reading a file", write_file: "Writing a file", edit_file: "Editing a file",
  multi_edit: "Editing a file", delete_file: "Deleting a file", glob: "Searching files",
  grep: "Searching files", preview_url: "Opening a preview", share_file: "Sharing a file",
};
const SANDBOX_TOOLS = new Set(Object.keys(SANDBOX_PHASES));

const fileName = (path: unknown) => String(path ?? "").split("/").filter(Boolean).pop() ?? "";

function describeSandbox(e: RunStep, running: boolean): StepDescription {
  const a = e.args;
  const facts = e.sandbox ?? {};
  const file = <Dim>{fileName(facts.path ?? a.path)}</Dim>;
  const said: React.ReactNode[] = ["Sandbox"];
  if (facts.exitCode != null) said.push(<>exit <b className="font-medium font-mono text-[var(--text-primary)]">{facts.exitCode}</b></>);
  if (facts.timedOut) said.push("timed out");
  if (facts.jobId) said.push(<>job <b className="font-medium font-mono text-[var(--text-primary)]">{facts.jobId}</b></>);
  const as = (icon: LucideIcon, sentence: React.ReactNode, more: Partial<StepDescription> = {}): StepDescription => ({
    icon: lucide(icon), sentence, phase: SANDBOX_PHASES[e.tool], facts: said, ...more,
  });
  switch (e.tool) {
    case "bash": {
      const what = String(a.description ?? "") || String(a.command ?? "").split("\n")[0].slice(0, 120);
      const verb = a.run_in_background ? "Started" : running ? "Running" : "Ran";
      return as(TerminalSquare, <>{verb} <Dim>{what}</Dim></>, { code: { label: "Command", text: String(a.command ?? "") } });
    }
    case "bash_output":
      return as(TerminalSquare, <>{running ? "Checking" : "Checked"} job <Dim>{String(a.job_id)}</Dim></>);
    case "bash_stop":
      return as(TerminalSquare, <>{running ? "Stopping" : "Stopped"} job <Dim>{String(a.job_id)}</Dim></>);
    case "read_file":
      return as(FileText, <>{running ? "Reading" : "Read"} {file}</>);
    case "write_file":
      return as(FilePen, <>{running ? "Writing" : "Wrote"} {file}</>, { code: { label: "Content", text: String(a.content ?? "") } });
    case "edit_file":
    case "multi_edit":
      return as(FilePen, <>{running ? "Editing" : "Edited"} {file}</>);
    case "delete_file":
      return as(Trash2, <>{running ? "Deleting" : "Deleted"} {file}</>);
    case "glob":
    case "grep":
      return as(Search, <>{running ? "Searching" : "Searched"} files for <Dim>{String(a.pattern ?? "")}</Dim></>);
    case "preview_url":
      return as(ExternalLink, <>{running ? "Opening" : "Opened"} port <Dim>{String(a.port)}</Dim></>, {
        extra: facts.url && (
          <a href={facts.url} target="_blank" rel="noreferrer" className="text-[12.5px] font-mono text-[var(--text-primary)] underline underline-offset-2 break-all">{facts.url}</a>
        ),
      });
    default: // share_file
      return as(Download, <>{running ? "Sharing" : "Shared"} {file}</>, { extra: e.file && <FileChip file={toAttachment(e.file)} /> });
  }
}

/** web_search and web_fetch (backend/agent/tools/web.py): what was looked for or read, and
    the sources the reply's `meta.web` numbers, as chips. */
function describeWeb(e: RunStep, running: boolean): StepDescription {
  const icon = lucide(e.tool === "web_search" ? Globe : FileText);
  const extra = e.web?.sources.length ? <SourceChips sources={e.web.sources} /> : undefined;
  if (e.tool === "web_search") {
    const query = String(e.args.query ?? "");
    const pagesRead = e.web?.read?.length ?? 0;
    const pagesAsked = Number(e.args.read) || 0;
    return {
      icon, extra,
      sentence: <>{running ? "Searching the web" : "Searched the web"}{query && <> for <Dim>{query}</Dim></>}{pagesRead > 0 && <Dim> · read {pages(pagesRead)}</Dim>}</>,
      phase: pagesAsked ? `Searching and reading ${pages(pagesAsked)}` : "Searching the web",
    };
  }
  const urls = Array.isArray(e.args.urls) ? e.args.urls.map(String) : [];
  const read = e.web?.sources.length || urls.length || 1;
  const one = urls.length === 1 ? pageName(urls[0]) : null;
  const phase = `Reading ${pages(urls.length || 1)}`;
  return {
    icon, extra, phase,
    sentence: <>{running ? "Reading" : "Read"} <Dim>{one ?? pages(running ? urls.length || 1 : read)}</Dim></>,
  };
}

const pages = (count: number) => `${count} page${count === 1 ? "" : "s"}`;

/** "https://www.hubspot.com/pricing/sales" as "hubspot.com/pricing/sales". */
function pageName(url: string): string {
  try {
    const { hostname, pathname } = new URL(url);
    return `${siteName(hostname)}${pathname === "/" ? "" : pathname}`;
  } catch {
    return url;
  }
}

// four, then "+N more"
const SHOWN_SOURCES = 4;

/** A web call's sources as chips: a favicon and the site, the page's title and link on hover. */
function SourceChips({ sources }: { sources: WebSource[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? sources : sources.slice(0, SHOWN_SOURCES);
  return (
    <div className="flex flex-wrap gap-1.5 min-w-0">
      {shown.map((source) => (
        <a
          key={source.n}
          href={webAddress(source.url)}
          target="_blank"
          rel="noreferrer"
          title={`${source.title}\n${source.url}`}
          className="press inline-flex items-center gap-1.5 h-7 pl-1.5 pr-2.5 max-w-[220px] rounded-[8px] bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[12.5px] text-[var(--text-primary)] min-w-0"
        >
          <SiteIcon url={source.url} site={source.site} />
          <span className="truncate">{siteName(source.site)}</span>
        </a>
      ))}
      {sources.length > SHOWN_SOURCES && (
        <button
          type="button"
          aria-expanded={all}
          onClick={() => setAll(!all)}
          className="press h-7 px-2.5 rounded-[8px] text-[12.5px] text-[var(--text-tertiary)] hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)] whitespace-nowrap"
        >
          {all ? "Show less" : `+${sources.length - SHOWN_SOURCES} more`}
        </button>
      )}
    </div>
  );
}
